import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AuthModal from "@/components/AuthModal";
import PaywallModal from "@/components/PaywallModal";
import TrendingSets from "@/components/TrendingSets";
import ShareQRSection from "@/components/ShareQRSection";
import { useAuth } from "@/context/AuthContext";
import { Zap, Waves, Music4, Cpu, Layers3, Radio } from "lucide-react";
import { detectUserCurrency, loadFxRates, formatLocalPreview, flagFor } from "@/lib/currency";

const HERO_IMG = "https://images.unsplash.com/photo-1544785349-c4a5301826fd?w=1600&q=80";
const CLUB_IMG = "https://images.unsplash.com/photo-1541126274323-dbac58d14741?w=1200&q=80";
const MIXER_IMG = "https://images.unsplash.com/photo-1594623930572-300a3011d9ae?w=1200&q=80";
const DECK_IMG = "https://images.unsplash.com/photo-1651065699236-6a6885503943?w=1200&q=80";

export default function Landing() {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [paywallOpen, setPaywallOpen] = useState(false);
  // Post-auth intent: "studio" (default) or "checkout" (open PaywallModal after
  // successful signup/login so PRO-CTA users don't accidentally land in the
  // studio with an unpaid trial and think they subscribed).
  const [postAuthIntent, setPostAuthIntent] = useState("studio");
  const [rates, setRates] = useState(null);
  const [localCurrency, setLocalCurrency] = useState("CAD");
  const navigate = useNavigate();

  useEffect(() => {
    setLocalCurrency(detectUserCurrency());
    loadFxRates().then(r => r && setRates(r));
  }, []);
  const monthlyPreview = formatLocalPreview(4.99, rates, localCurrency);

  // ─── Postcode ⇄ Zip code ⇄ Area code ⇄ Local ⇄ Kafunda ⇄ Bongo ──────
  // The line is: "Discovery is decided by the mix, not the ___".
  // Six variants keep the message inclusive for UK/CA (postcode), US (zip
  // code), countries that lean on "area code" phrasing, "local" (universal
  // slang for the neighbourhood joint — speaks to UK/IE/AU pub culture),
  // "kafunda" (Ugandan local-bar slang) and "bongo" (Tanzanian/Swahili
  // slang for Dar es Salaam & the local music scene). The last two speak
  // directly to the Africa-first Pesapal mobile-money flow. Pick a
  // sensible first word by locale/timezone, then rotate through every
  // 4 seconds so all six surface within a typical above-the-fold scroll.
  const LOCALE_WORDS = ["postcode", "zip code", "area code", "local", "kafunda", "bongo"];
  // Sunrise-sweep palette that rotates in lock-step with LOCALE_WORDS so
  // "the mix" gradient feels alive across each loop. No extra motion cost —
  // the existing key-driven fade already masks the swap.
  const MIX_GRADIENTS = [
    "linear-gradient(90deg, #00F0FF 0%, #FF8C00 55%, #FF2A6D 100%)", // postcode  · cyan → orange → magenta
    "linear-gradient(90deg, #FFB347 0%, #FF6B6B 50%, #FF2A6D 100%)", // zip code  · sunrise → pink
    "linear-gradient(90deg, #B6FF3C 0%, #3CFFC5 50%, #00F0FF 100%)", // area code · neon lime → cyan
    "linear-gradient(90deg, #A855F7 0%, #EC4899 50%, #F97316 100%)", // local     · violet → pink → amber (pub-after-dark)
    "linear-gradient(90deg, #FDE68A 0%, #FF6B35 45%, #DC2626 100%)", // kafunda   · lantern-amber → ember-red (East-African sundown)
    "linear-gradient(90deg, #00D4A3 0%, #0EA5E9 50%, #1E3A8A 100%)", // bongo     · Indian-Ocean teal → ocean blue (Dar es Salaam coast)
  ];
  const initialLocaleIndex = (() => {
    try {
      const locale = (navigator.language || "en-US").toLowerCase();
      // Timezone fallback — Chrome/Safari on African mobile carriers often
      // report navigator.language as "en-US" regardless of SIM country.
      // The IANA timezone is set from the OS clock + SIM/GPS and is far
      // more reliable for first-paint personalization.
      let tz = "";
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* older browsers */ }

      // Uganda — lead with "kafunda" (local-bar slang) so Ugandans feel seen first
      if (locale.includes("-ug") || tz === "Africa/Kampala") return 4;

      // Tanzania — lead with "bongo" (Swahili slang for Dar/the TZ scene)
      if (locale.includes("-tz") || tz === "Africa/Dar_es_Salaam") return 5;

      // Rest of East Africa → "local" first (same vibe, universally
      // understood). Covers Kenya, Rwanda, Burundi, S. Sudan, Ethiopia +
      // Nairobi/Kigali/Addis timezone hits.
      const eastAfricaLocales = ["-ke", "-rw", "-bi", "-ss", "-et"];
      const eastAfricaTzs = [
        "Africa/Nairobi", "Africa/Kigali",
        "Africa/Bujumbura", "Africa/Juba", "Africa/Addis_Ababa",
        "Africa/Mogadishu", "Indian/Antananarivo",
      ];
      if (eastAfricaLocales.some(l => locale.includes(l)) || eastAfricaTzs.includes(tz)) return 3;

      // UK / Ireland / Australia / NZ — colloquial "local" means your pub
      if (locale.endsWith("-gb") || locale.includes("-ie") || locale.endsWith("-au") || locale.endsWith("-nz")) return 3;
      if (locale.endsWith("-us") || locale === "en-us" || locale.includes("-ph")) return 1;
      // Remaining African English markets (SA, NG, GH) lean on "area code"
      if (locale.includes("-za") || locale.includes("-ng") || locale.includes("-gh")) return 2;
      return 0;
    } catch { return 0; }
  })();
  const [localeWordIdx, setLocaleWordIdx] = useState(initialLocaleIndex);
  useEffect(() => {
    const iv = setInterval(() => setLocaleWordIdx(i => (i + 1) % LOCALE_WORDS.length), 4 * 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const currentLocaleWord = LOCALE_WORDS[localeWordIdx];

  const openAuth = (mode, intent = "studio") => { setAuthMode(mode); setPostAuthIntent(intent); setAuthOpen(true); };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white relative overflow-x-hidden">
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-[#1E2638] bg-[#0B0E14]/85 backdrop-blur-md px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Radio className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">
            NEURAL<span className="text-[#00F0FF]">DECK</span>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 font-cond uppercase text-sm tracking-widest text-[#94A3B8]">
          <a href="#features" className="hover:text-[#00F0FF] transition-colors">Features</a>
          <a href="#pricing" className="hover:text-[#00F0FF] transition-colors">Pricing</a>
          <a href="#tech" className="hover:text-[#00F0FF] transition-colors">Tech</a>
          <Link to="/download" data-testid="nav-download-link" className="hover:text-[#00F0FF] transition-colors">Download Bridge</Link>
          <Link to="/how-to" data-testid="nav-howto-link" className="hover:text-[#00F0FF] transition-colors">How to Use</Link>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <span className="hidden sm:inline text-xs font-mono text-[#94A3B8]" data-testid="header-user-name">{user.display_name}{user.is_premium && " · PRO"}</span>
              <Button data-testid="open-studio-button" onClick={() => navigate("/studio")} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-extrabold uppercase tracking-widest text-xs">Enter Studio</Button>
              <Button data-testid="header-settings-button" onClick={() => navigate("/settings")} variant="outline"
                className="border-[#1E2638] hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent text-[#94A3B8] font-black uppercase tracking-widest text-xs" title="Settings · MIDI · Payouts · Tip jar">
                Settings
              </Button>
              <Button data-testid="logout-button" onClick={logout} variant="ghost" className="text-[#94A3B8] hover:text-white text-xs">Logout</Button>
            </>
          ) : (
            <>
              <Button data-testid="header-login-button" variant="ghost" onClick={() => openAuth("login")} className="text-[#94A3B8] hover:text-white text-xs uppercase tracking-widest">Login</Button>
              <Button data-testid="header-signup-button" onClick={() => openAuth("signup")} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-extrabold uppercase tracking-widest text-xs">Get Started</Button>
            </>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#00F0FF]" data-testid="hero-badge">
              <span className="w-2 h-2 rounded-full bg-[#00F0FF] neon-pulse"/> A DJ economy in the browser
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="hero-title">
              Discovery is decided by<br/>
              <span key={`mix-${localeWordIdx}`}
                    data-testid="hero-mix-word"
                    className="text-transparent bg-clip-text animate-fade-in"
                    style={{ backgroundImage: MIX_GRADIENTS[localeWordIdx] }}>
                the mix
              </span>,<br/>
              not the&nbsp;
              <span key={currentLocaleWord}
                    data-testid="hero-locale-word"
                    className="inline-block animate-fade-in">
                {currentLocaleWord}.
              </span>
            </h1>
            <p className="text-lg text-[#94A3B8] max-w-xl leading-relaxed">
              Four decks, an AI co-pilot, and one-tap live streaming to the whole world — from any browser. Tips + merch route straight to your bank. No hardware, no gatekeepers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button data-testid="hero-cta-launch" onClick={() => (user ? navigate("/studio") : openAuth("signup"))} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest px-8 py-6 text-sm shadow-[0_0_30px_rgba(0,240,255,0.5)]">
                Open the booth
              </Button>
              <Button data-testid="hero-cta-demo" variant="outline" onClick={() => navigate("/studio")} className="border-[#2A354B] hover:border-[#00F0FF] bg-transparent text-white hover:text-[#00F0FF] font-black uppercase tracking-widest px-8 py-6 text-sm">
                Try free demo
              </Button>
              <Button data-testid="hero-cta-howto" variant="ghost" onClick={() => navigate("/how-to")} className="text-[#94A3B8] hover:text-[#00F0FF] font-black uppercase tracking-widest px-4 py-6 text-sm">
                How to use →
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 pt-4 font-mono text-xs text-[#94A3B8] uppercase tracking-widest">
              <span>· 4 decks · AI co-pilot</span>
              <span>· Live · Tips · Merch</span>
              <span>· AI Recommender</span>
              <span>· Auto Beat-Match</span>
            </div>
            <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[11px] font-mono uppercase tracking-widest text-[#00F0FF]"
                 data-testid="landing-pioneer-badge">
              <Radio className="w-3 h-3"/>
              Works with Pioneer DJ · DDJ / CDJ / XDJ (MIDI mode)
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#2A354B] shadow-[0_0_80px_rgba(0,240,255,0.25)]">
              <img src={HERO_IMG} alt="DJ Turntable" className="w-full h-[520px] object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/20 to-transparent"/>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-2">// Live Session · 128.0 BPM · 8A</div>
                <div className="font-display font-black text-2xl">4 decks running · zero latency</div>
              </div>
              <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-[#00F0FF]/40 font-mono text-[10px] tracking-widest text-[#00F0FF]">REC</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="px-6 py-24 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12 gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// Features</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Built like real hardware. Smarter than it.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <FeatureCard testid="feature-4deck" className="md:col-span-2 lg:row-span-2" accent="#00F0FF" icon={<Layers3 className="w-6 h-6"/>} title="4 Decks in a 2×2 Grid" body="Layer four tracks simultaneously with an oversized central mixer. Each deck runs an independent waveform, EQ pod and cue matrix.">
            <img src={MIXER_IMG} alt="Mixer" className="mt-6 rounded-lg w-full h-56 object-cover border border-[#1E2638]"/>
          </FeatureCard>
          <FeatureCard testid="feature-waveform" accent="#FF8C00" icon={<Waves className="w-6 h-6"/>} title="Live Multi-Band Waveforms" body="Frequency-colored Canvas rendering with beat-gridded playhead scrubbing."/>
          <FeatureCard testid="feature-ai" accent="#FF2A6D" icon={<Cpu className="w-6 h-6"/>} title="Neural Transition AI" body="GPT-powered next-track picker scores harmonic + BPM compatibility and coaches the crossfade."/>
          <FeatureCard testid="feature-sources" accent="#00FF66" icon={<Music4 className="w-6 h-6"/>} title="Multi-Source Library" body="Upload local MP3/WAV, search YouTube by keyword, curated demo tracks always on tap."/>
          <FeatureCard testid="feature-sync" accent="#FFC107" icon={<Zap className="w-6 h-6"/>} title="One-Tap SYNC" body="Auto beat-match any two decks with pitch adjust suggestions to 0.01% precision.">
            <img src={DECK_IMG} alt="Deck" className="mt-6 rounded-lg w-full h-40 object-cover border border-[#1E2638]"/>
          </FeatureCard>
          <FeatureCard testid="feature-pioneer" accent="#00F0FF" icon={<Radio className="w-6 h-6"/>} title="Pioneer DJ Ready" body="Plug in a DDJ, CDJ or XDJ (MIDI mode) and NeuralDeck auto-maps play, cue, tempo, EQs, hot cues + loops. Every Pioneer controller from 2006 onward — plus MIDI Learn for the rest."/>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-6 py-24 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// Pricing</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Free to spin. PRO to save.</h2>
          {localCurrency !== "CAD" && (
            <div className="mt-3 text-xs font-mono uppercase tracking-widest text-[#94A3B8]" data-testid="pricing-locale-note">
              Showing prices in <span className="text-[#00F0FF]">CAD</span> + your local <span className="text-[#00F0FF]">{localCurrency}</span> equivalent
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#1E2638] bg-[#121620] p-8 space-y-4" data-testid="pricing-free">
            <div className="font-mono text-xs uppercase tracking-widest text-[#94A3B8]">Free · Demo</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-5xl">$0</span>
              <span className="text-[#94A3B8] text-sm">/ forever</span>
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">Same price everywhere on Earth 🌍</div>
            <ul className="space-y-2 text-[#94A3B8] text-sm">
              <li>· All 4 decks · full mixer</li>
              <li>· Demo library + YouTube search</li>
              <li>· Local file upload</li>
              <li>· AI recommender (limited)</li>
            </ul>
            <Button data-testid="pricing-free-cta" onClick={() => (user ? navigate("/studio") : openAuth("signup"))} className="w-full bg-transparent border border-[#2A354B] hover:bg-[#1A202C] text-white font-black uppercase tracking-widest">Start Mixing Free</Button>
          </div>
          <div className="rounded-2xl border-2 border-[#00F0FF] bg-gradient-to-br from-[#121620] to-[#0B0E14] p-8 space-y-4 relative overflow-hidden shadow-[0_0_60px_rgba(0,240,255,0.15)]" data-testid="pricing-pro">
            <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-[#00F0FF] text-black px-2 py-0.5 rounded">RECOMMENDED</div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#00F0FF]">PRO · Monthly</div>
            {/* Primary price tile — CAD, the only currency Stripe actually charges */}
            <div className="rounded-xl border border-[#00F0FF]/40 bg-[#00F0FF]/5 p-4">
              <div className="flex items-baseline gap-2">
                <span className="font-display font-black text-5xl">CA$4.99</span>
                <span className="text-[#94A3B8] text-sm">/mo</span>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded px-1.5 py-0.5">🇨🇦 · Primary · CAD</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mt-2">Billed in Canadian dollars via Stripe</div>
            </div>
            {/* Secondary price tile — visitor's own currency, purely informational */}
            {monthlyPreview && (
              <div className="rounded-xl border border-[#1E2638] bg-[#0B0E14]/60 p-4" data-testid="landing-pro-local">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-3xl text-[#F8FAFC]">≈ {monthlyPreview.replace(` ${localCurrency}`, "")}</span>
                  <span className="text-[#94A3B8] text-xs">/mo</span>
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] border border-[#2A354B] rounded px-1.5 py-0.5" data-testid="landing-pro-local-badge">{flagFor(localCurrency)} · Your currency · {localCurrency}</span>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mt-2">Preview only · your bank converts at their FX rate at checkout</div>
              </div>
            )}
            <div className="text-xs font-mono uppercase tracking-widest text-[#00FF66]">7-day free trial · cancel anytime</div>
            <ul className="space-y-2 text-white text-sm">
              <li>· Save unlimited mixes & sessions</li>
              <li>· Full AI harmonic engine (unlimited)</li>
              <li>· Cloud sync across devices</li>
              <li>· Cancel anytime</li>
            </ul>
            <Button data-testid="pricing-pro-cta" onClick={() => (user ? setPaywallOpen(true) : openAuth("signup", "checkout"))} className="w-full bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">{user?.is_premium && !user?.trial ? "Manage PRO" : "Upgrade to PRO"}</Button>
          </div>
        </div>
      </section>

      {/* TECH BAND */}
      <TrendingSets/>

      <section id="tech" className="px-6 py-20 border-t border-[#1E2638] max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 font-mono text-xs uppercase tracking-widest text-[#94A3B8]">
          <div><span className="text-[#00F0FF] block mb-1">// Camelot</span>Harmonic key wheel</div>
          <div><span className="text-[#FF8C00] block mb-1">// Web Audio</span>Real-time DSP</div>
          <div><span className="text-[#FF2A6D] block mb-1">// GPT 5.6 Terra</span>AI mix coach</div>
          <div><span className="text-[#00FF66] block mb-1">// Stripe</span>Secure premium billing</div>
        </div>
      </section>

      <ShareQRSection/>

      <footer className="border-t border-[#1E2638] px-6 py-8 text-center font-mono text-xs text-[#94A3B8]">
        <div>© 2026 Katwire Labs · NeuralDeck AI · Made for people who move the crowd.</div>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1" data-testid="footer-contact">
          <span>Contact: <a href="mailto:akistam@gmail.com" className="text-[#00F0FF] hover:underline" data-testid="footer-contact-email">akistam@gmail.com</a></span>
          <span className="text-[#94A3B8]/60 hidden sm:inline">·</span>
          <span className="text-[#94A3B8] italic">Domain + support team rolling out soon</span>
        </div>
        <div className="mt-2 flex justify-center gap-4">
          <a href="/privacy" className="hover:text-[#00F0FF]">Privacy</a>
          <span>·</span>
          <a href="/terms" className="hover:text-[#00F0FF]">Terms</a>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} onSuccess={() => { if (postAuthIntent === "checkout") { setPaywallOpen(true); setPostAuthIntent("studio"); } else { navigate("/studio"); } }}/>
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen}/>
    </div>
  );
}

function FeatureCard({ children, icon, title, body, accent, className = "", testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border border-[#1E2638] bg-[#121620] p-6 hover:border-[#2A354B] transition-colors ${className}`}>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}55` }}>{icon}</div>
      <div className="font-display font-black text-xl mb-2">{title}</div>
      <div className="text-sm text-[#94A3B8] leading-relaxed">{body}</div>
      {children}
    </div>
  );
}
